import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { AdvancedOptions } from '../../../../common/types';
import {
	AdvancedOptionsSetting,
	FileBrowser,
	FileDropTarget,
} from '../../../../common/ui/file-dropzone';
import { DropzoneContent } from '../../../../common/ui/file-dropzone/dropzone-content';
import { useAdvancedOptions, useMappingState } from '../../../../controllers/import-state';
import { useCSVDropzone } from '../../../../controllers/utils/use-csv-dropzone';

type Props = {
	isLeaner?: boolean;
};

export const Dropzone = ({ isLeaner = false }: Props) => {
	const [advancedOptions, { updateAdvancedOptions }] = useAdvancedOptions();
	const [mappingState, { setMappingState }] = useMappingState();

	const {
		isFileBrowserOpen,
		isFileNotCSV,
		isFileTooLarge,
		isFileUploading,
		hasErrorOccurred,
		uploadedFileName,
		onReceiveFile,
		onRemoveFile,
		onCloseFileBrowser,
		onOpenFileBrowser,
		onFileDraggedAndDropped,
	} = useCSVDropzone();

	const onChange = useCallback(
		(data: Partial<AdvancedOptions>) => {
			updateAdvancedOptions(data);
			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}
		},
		[mappingState, setMappingState, updateAdvancedOptions],
	);

	return (
		<Stack>
			<DropzoneTarget
				onReceiveFile={onFileDraggedAndDropped}
				onClick={onOpenFileBrowser}
				isDisabled={isFileUploading}
				isLeaner={isLeaner}
			>
				<DropzoneContent
					isFileUploading={isFileUploading}
					isFileNotCSV={isFileNotCSV}
					hasErrorOccurred={hasErrorOccurred}
					isFileTooLarge={isFileTooLarge}
					uploadedFileName={uploadedFileName}
					onRemoveFile={onRemoveFile}
					isLeaner={isLeaner}
				/>
			</DropzoneTarget>
			<FileBrowser
				isOpen={isFileBrowserOpen}
				onReceiveFile={onReceiveFile}
				onClose={onCloseFileBrowser}
			/>
			<AdvancedOptionsSetting advancedOptions={advancedOptions} onChange={onChange} />
		</Stack>
	);
};

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DropzoneTarget = styled(FileDropTarget)<{ isLeaner: boolean }>({
	margin: `${token('space.200', '16px')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ isLeaner }) => (isLeaner ? '150px' : '200px'),
	padding: token('space.200', '16px'),
});
